import React, { useState } from 'react';
import {
  Box, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Typography, Switch, FormControlLabel, Divider, Tooltip,
  CircularProgress
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import TerrainIcon from '@mui/icons-material/Terrain';
import PublicIcon from '@mui/icons-material/Public';
import LandscapeIcon from '@mui/icons-material/Landscape';

const TerrainPicker = ({
  terrainProviders,
  currentProvider,
  terrainEnabled,
  terrainLoading,
  onProviderChange,
  onTerrainToggle
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProviderSelect = (providerId) => {
    onProviderChange(providerId);
    handleMenuClose();
  };

  const getProviderIcon = (provider) => {
    switch (provider.icon) {
      case 'terrain': return <TerrainIcon fontSize="small" />;
      case 'landscape': return <LandscapeIcon fontSize="small" />;
      case 'public': return <PublicIcon fontSize="small" />;
      default: return <TerrainIcon fontSize="small" />;
    }
  };

  const currentProviderConfig = terrainProviders.find(p => p.id === currentProvider);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 950,
        mt: '180px', // Position below other controls (adjusted for your layout)
      }}
    >
      <Tooltip title="Terrain Settings" placement="left">
        <IconButton
          onClick={handleMenuOpen}
          size="large"
          disabled={terrainLoading}
          sx={{
            backgroundColor: alpha(theme.palette.background.paper, 0.85),
            color: terrainEnabled ? theme.palette.primary.main : 'inherit',
            backdropFilter: 'blur(4px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
              transform: 'translateY(-1px)'
            },
          }}
        >
          {terrainLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <TerrainIcon sx={{ fontSize: '1.2rem' }} />
          )}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            borderRadius: '12px',
            minWidth: 220,
            mt: 1,
          }
        }}
      >
        {/* Terrain Toggle */}
        <Box sx={{ p: 2, pb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={terrainEnabled}
                onChange={onTerrainToggle}
                disabled={terrainLoading}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Enable Terrain
              </Typography>
            }
          />
        </Box>

        <Divider sx={{ mx: 1 }} />

        {/* Current Provider Info */}
        {currentProviderConfig && (
          <Box sx={{ p: 2, pt: 1.5, pb: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Current: {currentProviderConfig.name}
            </Typography>
          </Box>
        )}

        <Divider sx={{ mx: 1 }} />

        {/* Provider Options */}
        {terrainProviders.map((provider) => (
          <MenuItem
            key={provider.id}
            onClick={() => handleProviderSelect(provider.id)}
            selected={provider.id === currentProvider}
            disabled={terrainLoading}
            sx={{
              mx: 1,
              mb: 0.5,
              borderRadius: '8px',
              '&:last-child': { mb: 1 }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {getProviderIcon(provider)}
            </ListItemIcon>
            <ListItemText
              primary={provider.name}
              secondary={provider.description}
              secondaryTypographyProps={{
                variant: 'caption',
                sx: { fontSize: '0.7rem' }
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default TerrainPicker; 